/**
 * Container Component
 *
 * A max-width container component that provides consistent horizontal
 * padding and centering for page content.
 *
 * Uses CSS custom properties (design tokens) for spacing values,
 * ensuring consistency across the design system.
 *
 * @module components/core/Container
 *
 * @example
 * ```tsx
 * // Default container (lg max-width, centered, medium padding)
 * <Container>
 *   <PageContent />
 * </Container>
 *
 * // Full-width container with no padding
 * <Container size="full" padding="none">
 *   <HeroSection />
 * </Container>
 *
 * // Small container for narrow content (forms, etc.)
 * <Container size="sm" padding="lg">
 *   <LoginForm />
 * </Container>
 *
 * // Polymorphic usage
 * <Container as="section" size="xl">
 *   <ArticleContent />
 * </Container>
 * ```
 */

import { type ElementType, type ReactNode, type CSSProperties, forwardRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Container size options
 */
export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

/**
 * Container padding options
 */
export type ContainerPadding = 'none' | 'sm' | 'md' | 'lg';

/**
 * Props for the Container component
 */
export interface ContainerProps {
  /**
   * Child elements to render inside the container
   */
  children: ReactNode;

  /**
   * Maximum width of the container
   * - sm: 640px (40rem) - Narrow content, forms
   * - md: 768px (48rem) - Medium content
   * - lg: 1024px (64rem) - Standard page content
   * - xl: 1280px (80rem) - Wide content
   * - 2xl: 1536px (96rem) - Extra wide content
   * - full: 100% - No max-width restriction
   * @default 'lg'
   */
  size?: ContainerSize;

  /**
   * Horizontal padding
   * - none: 0
   * - sm: var(--spacing-sm)
   * - md: var(--spacing-md)
   * - lg: var(--spacing-lg)
   * @default 'md'
   */
  padding?: ContainerPadding;

  /**
   * Whether to center the container horizontally
   * @default true
   */
  centered?: boolean;

  /**
   * The HTML element or React component to render as
   * @default 'div'
   */
  as?: ElementType;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Inline styles (use sparingly - prefer tokens)
   */
  style?: CSSProperties;

  /**
   * Optional data-testid for testing
   */
  'data-testid'?: string;
}

/**
 * Map container sizes to CSS max-width values
 * Using rem values for consistency with design system
 */
const sizeMap: Record<ContainerSize, string> = {
  sm: '40rem',    // 640px
  md: '48rem',    // 768px
  lg: '64rem',    // 1024px
  xl: '80rem',    // 1280px
  '2xl': '96rem', // 1536px
  full: '100%',
};

/**
 * Map container sizes to Tailwind max-width classes
 * (Alternative approach using utility classes)
 */
const sizeTailwindMap: Record<ContainerSize, string> = {
  sm: 'max-w-screen-sm',
  md: 'max-w-screen-md',
  lg: 'max-w-screen-lg',
  xl: 'max-w-screen-xl',
  '2xl': 'max-w-screen-2xl',
  full: 'max-w-full',
};

/**
 * Map padding values to CSS custom property values
 */
const paddingMap: Record<ContainerPadding, string> = {
  none: '0',
  sm: 'var(--spacing-sm)',
  md: 'var(--spacing-md)',
  lg: 'var(--spacing-lg)',
};

/**
 * Container Component
 *
 * A polymorphic max-width container component that provides consistent
 * horizontal padding and centering using design system tokens.
 */
export const Container = forwardRef<HTMLElement, ContainerProps>(function Container(
  {
    children,
    size = 'lg',
    padding = 'md',
    centered = true,
    as: Component = 'div',
    className,
    style,
    'data-testid': testId,
    ...restProps
  },
  ref
) {
  const combinedStyle: CSSProperties = {
    maxWidth: sizeMap[size],
    paddingLeft: paddingMap[padding],
    paddingRight: paddingMap[padding],
    ...style,
  };

  return (
    <Component
      ref={ref}
      className={cn(
        'w-full',
        centered && 'mx-auto',
        className
      )}
      style={combinedStyle}
      data-testid={testId}
      {...restProps}
    >
      {children}
    </Component>
  );
});

Container.displayName = 'Container';

/**
 * Props for the Prose container variant
 */
export interface ProseContainerProps extends Omit<ContainerProps, 'size'> {
  /**
   * Maximum width - optimized for reading
   * - narrow: 45ch (~540px) - Optimal for body text
   * - default: 65ch (~780px) - Standard prose
   * - wide: 80ch (~960px) - Wide prose with images
   * @default 'default'
   */
  width?: 'narrow' | 'default' | 'wide';
}

/**
 * Map prose widths to CSS values
 */
const proseWidthMap: Record<'narrow' | 'default' | 'wide', string> = {
  narrow: '45ch',
  default: '65ch',
  wide: '80ch',
};

/**
 * ProseContainer Component
 *
 * A specialized container optimized for readable text content.
 * Uses character-based widths (ch units) for optimal line length.
 *
 * @example
 * ```tsx
 * <ProseContainer>
 *   <article>
 *     <h1>Article Title</h1>
 *     <p>Long form content that benefits from optimal line length...</p>
 *   </article>
 * </ProseContainer>
 * ```
 */
export const ProseContainer = forwardRef<HTMLElement, ProseContainerProps>(
  function ProseContainer(
    {
      children,
      width = 'default',
      padding = 'md',
      centered = true,
      as: Component = 'div',
      className,
      style,
      'data-testid': testId,
      ...restProps
    },
    ref
  ) {
    const combinedStyle: CSSProperties = {
      maxWidth: proseWidthMap[width],
      paddingLeft: paddingMap[padding],
      paddingRight: paddingMap[padding],
      ...style,
    };

    return (
      <Component
        ref={ref}
        className={cn(
          'w-full',
          centered && 'mx-auto',
          className
        )}
        style={combinedStyle}
        data-testid={testId}
        {...restProps}
      >
        {children}
      </Component>
    );
  }
);

ProseContainer.displayName = 'ProseContainer';

export default Container;
